/**
 * SSE Event Helper Utilities
 * Provides utility functions for parsing and validating SSE events
 *
 * @module common/helpers/sse-event.helper
 */

import type {
  ChatSessionPayload,
  FollowUpQueriesPayload,
  MessageChunkPayload,
  ParsedSSEEvent,
  ProcessingStepPayload,
  SSEError,
  SSEEvent,
  SSEEventType,
} from "~/common/types";

import { SSE_EVENT_TYPE } from "~/common/constants";

/**
 * SSE Event Helper Class
 * Provides static methods for SSE event handling
 */
export class SSEEventHelper {
  /**
   * Validates if a string is a valid SSE event type
   *
   * @param eventType - Event type string to validate
   * @returns True if valid SSE event type
   *
   * @example
   * ```typescript
   * SSEEventHelper.isValidEventType('message'); // true
   * SSEEventHelper.isValidEventType('invalid'); // false
   * ```
   */
  static isValidEventType(eventType: string): eventType is SSEEventType {
    return Object.values(SSE_EVENT_TYPE).includes(eventType as SSEEventType);
  }

  /**
   * Parses SSE event data field from JSON string
   *
   * @param data - JSON string from SSE event data field
   * @returns Parsed object or null if parsing fails
   *
   * @example
   * ```typescript
   * const payload = SSEEventHelper.parseEventData('{"content": "Hello"}');
   * console.log(payload?.content); // "Hello"
   * ```
   */
  static parseEventData<T = unknown>(data?: string): T | null {
    if (!data) return null;

    try {
      return JSON.parse(data) as T;
    } catch (error) {
      console.error("Failed to parse SSE event data:", error);
      return null;
    }
  }

  /**
   * Parses raw SSE event into typed structure
   *
   * @param event - Raw SSE event
   * @returns Parsed SSE event with typed payload or null if invalid
   *
   * @example
   * ```typescript
   * const parsed = SSEEventHelper.parseSSEEvent({
   *   event: 'message',
   *   data: '{"content": "Hi", "index": 0}',
   * });
   *
   * if (parsed?.event === 'message') {
   *   console.log(parsed.payload.content); // "Hi"
   * }
   * ```
   */
  static parseSSEEvent(event: SSEEvent): ParsedSSEEvent | null {
    if (!this.isValidEventType(event.event)) {
      console.warn("Invalid SSE event type:", event.event);
      return null;
    }

    switch (event.event) {
      case SSE_EVENT_TYPE.CHAT_SESSION: {
        const payload = this.parseEventData<ChatSessionPayload>(event.data);
        if (!payload) return null;
        return { event: SSE_EVENT_TYPE.CHAT_SESSION, payload };
      }

      case SSE_EVENT_TYPE.STEP: {
        const payload = this.parseEventData<ProcessingStepPayload>(event.data);
        if (!payload) return null;
        return { event: SSE_EVENT_TYPE.STEP, payload };
      }

      case SSE_EVENT_TYPE.MESSAGE: {
        const payload = this.parseEventData<MessageChunkPayload>(event.data);
        if (!payload) return null;
        return { event: SSE_EVENT_TYPE.MESSAGE, payload };
      }

      case SSE_EVENT_TYPE.FOLLOW_UP_QUERIES: {
        const payload = this.parseEventData<FollowUpQueriesPayload>(event.data);
        if (!payload) return null;
        return { event: SSE_EVENT_TYPE.FOLLOW_UP_QUERIES, payload };
      }

      case SSE_EVENT_TYPE.END: {
        return { event: SSE_EVENT_TYPE.END };
      }

      default:
        return null;
    }
  }

  /**
   * Creates an SSE error object with standardized structure
   *
   * @param error - Error object or message
   * @param statusCode - Optional HTTP status code
   * @returns Structured SSE error object
   *
   * @example
   * ```typescript
   * const error = SSEEventHelper.createError(
   *   new Error('Connection failed'),
   *   503
   * );
   * ```
   */
  static createError(error: unknown, statusCode?: number): SSEError {
    if (error instanceof Error) {
      return {
        name: error.name,
        message: error.message,
        statusCode,
        originalError: error,
      };
    }

    return {
      name: "SSEError",
      message: String(error),
      statusCode,
      originalError: error,
    };
  }

  /**
   * Checks if error is an abort error (user cancelled)
   *
   * @param error - Error object to check
   * @returns True if error is abort type
   *
   * @example
   * ```typescript
   * if (SSEEventHelper.isAbortError(error)) {
   *   console.log('Stream was cancelled by user');
   * }
   * ```
   */
  static isAbortError(error: unknown): boolean {
    if (error instanceof Error) {
      return (
        error.name === "AbortError" ||
        error.message.toLowerCase().includes("abort")
      );
    }
    return false;
  }

  /**
   * Validates chat session payload structure
   *
   * @param payload - Payload to validate
   * @returns True if valid chat session payload
   */
  static isValidChatSessionPayload(
    payload: unknown,
  ): payload is ChatSessionPayload {
    if (!payload || typeof payload !== "object") return false;

    const p = payload as Record<string, unknown>;
    return typeof p.id === "string" && typeof p.name === "string";
  }

  /**
   * Validates message chunk payload structure
   *
   * @param payload - Payload to validate
   * @returns True if valid message chunk payload
   */
  static isValidMessageChunkPayload(
    payload: unknown,
  ): payload is MessageChunkPayload {
    if (!payload || typeof payload !== "object") return false;

    const p = payload as Record<string, unknown>;
    return typeof p.content === "string" && typeof p.index === "number";
  }

  /**
   * Validates processing step payload structure
   *
   * @param payload - Payload to validate
   * @returns True if valid processing step payload
   */
  static isValidProcessingStepPayload(
    payload: unknown,
  ): payload is ProcessingStepPayload {
    if (!payload || typeof payload !== "object") return false;

    const p = payload as Record<string, unknown>;
    return typeof p.step === "string";
  }

  /**
   * Formats event for logging/debugging
   *
   * @param event - SSE event to format
   * @returns Formatted string representation
   */
  static formatEventForLog(event: SSEEvent | ParsedSSEEvent): string {
    if ("payload" in event) {
      return `[SSE:${event.event}] ${JSON.stringify(event.payload)}`;
    }

    const sseEvent = event as SSEEvent;
    return `[SSE:${sseEvent.event}] ${sseEvent.data || "no data"}`;
  }
}
